// Phase 1 sandbox origin: which Host is the Re:Vault shell and which is the sandbox origin S.
//
// Proxied third-party pages (and the engines that serve them) move to a sibling origin
// `<label>.<zone>` (label "view", chosen once by the owner and recorded in .rvprefixes.json as
// `sandboxLabel`). Same site as the shell, different origin, so the browser's same-origin policy
// keeps a proxied page away from the shell's storage, token and windows. S holds no Re:Vault data.
//
// Pure: no I/O, no globals. The server (host gate, headers, /account/config), the tests and the
// nginx/DNS proposal (docs/security/PHASE1-NGINX.md, PHASE1-DNS.md) all read the one zone list.
// Only the Re:Vault first-party zones get a sandbox in Phase 1. bop.zone is first-party in the
// stream router too, but it is the game, not Re:Vault, so it is not here. BYOD hosts stay legacy
// (same-origin, as today) until Phase 2.

export const DEFAULT_SANDBOX_LABEL = "view";

export const FIRST_PARTY_SANDBOX_ZONES = Object.freeze([
	"endis.rest",
	"endis.org",
	"fairfieldps.org",
	"greenwichps.org",
	"hartfordps.org",
	"lunoluno.org",
	"newhavenps.org",
	"newyorkps.org",
	"reformdecor.org",
	"abc123publicschools.org",
	"endiseducation.org",
]);

const LABEL_RE = /^[a-z][a-z0-9-]{0,30}$/;
// A plain DNS name with an optional port: the same shape server/index.js accepts for the Study
// Hall CSP (HALL_HOST_RE). Anything else (CRLF, quotes, userinfo, brackets) is not a host we
// classify, and a host we cannot classify is the shell (see hostRole).
const HOST_RE = /^[a-z0-9-]+(?:\.[a-z0-9-]+)*(?::\d{1,5})?$/i;

// A label from the build manifest or the ledger, or the default when it is missing or malformed.
export function normLabel(label) {
	const l = String(label == null ? "" : label).trim().toLowerCase();
	return LABEL_RE.test(l) ? l : DEFAULT_SANDBOX_LABEL;
}

// "View.Endis.REST:8443" -> { name: "view.endis.rest", port: "8443" }; null when malformed.
// One trailing dot (the fully qualified form "view.endis.rest.") is the same name: browsers send
// it as typed and nginx's $host strips it, so it must not turn a sandbox Host into a shell one
// when a request reaches the app without that normalization.
export function parseHost(host) {
	const h = String(host == null ? "" : host).replace(/([^.:])\.(:\d{1,5})?$/, "$1$2");
	if (!h || h.length > 260 || !HOST_RE.test(h)) return null;
	const i = h.indexOf(":");
	const name = (i === -1 ? h : h.slice(0, i)).toLowerCase();
	const port = i === -1 ? "" : h.slice(i + 1);
	if (name.length > 253) return null;
	return { name, port };
}

// The first-party zone a bare hostname belongs to (the zone itself or any name under it), or "".
// Longest match wins, so a test zone nested under a real one still resolves to itself.
export function zoneOf(name, zones = FIRST_PARTY_SANDBOX_ZONES) {
	let best = "";
	for (const z of zones) {
		if ((name === z || name.endsWith("." + z)) && z.length > best.length) best = z;
	}
	return best;
}

// "sandbox" when the Host is exactly `<label>.<zone>` for a first-party zone, else "shell".
// A malformed Host is "shell": the sandbox role only REMOVES surface, but the shell is what every
// existing client and test expects, and a weird Host must not change which headers it gets.
export function hostRole(host, label = DEFAULT_SANDBOX_LABEL, zones = FIRST_PARTY_SANDBOX_ZONES) {
	const h = parseHost(host);
	if (!h) return "shell";
	const l = normLabel(label);
	if (!h.name.startsWith(l + ".")) return "shell";
	const rest = h.name.slice(l.length + 1);
	return zones.includes(rest) ? "sandbox" : "shell";
}

// The sandbox sibling of a shell host: endis.rest, www.endis.rest and any other name under a
// first-party zone map to `<label>.<zone>` (same site, so storage is not partitioned and the
// iframe is same-site). The port is kept (local two-origin testing); production Hosts carry none.
// "" for a BYOD host, a sandbox host itself, or a malformed one.
export function sandboxHostFor(shellHost, label = DEFAULT_SANDBOX_LABEL, zones = FIRST_PARTY_SANDBOX_ZONES) {
	const h = parseHost(shellHost);
	if (!h) return "";
	const l = normLabel(label);
	if (hostRole(shellHost, l, zones) === "sandbox") return "";
	const z = zoneOf(h.name, zones);
	if (!z) return "";
	// A shell name that is itself under the sandbox name (view.x.endis.rest is not possible for a
	// listed zone, but a nested test zone could produce one) is not a shell we pair.
	if (h.name.endsWith("." + l + "." + z)) return "";
	return l + "." + z + (h.port ? ":" + h.port : "");
}

// The shell apex for a sandbox host (view.endis.rest -> endis.rest), or "" when it is not one.
export function shellHostForSandbox(sandboxHost, label = DEFAULT_SANDBOX_LABEL, zones = FIRST_PARTY_SANDBOX_ZONES) {
	const h = parseHost(sandboxHost);
	if (!h || hostRole(sandboxHost, label, zones) !== "sandbox") return "";
	return h.name.slice(normLabel(label).length + 1) + (h.port ? ":" + h.port : "");
}

// True for ANY host whose first label is the sandbox label (view.endis.rest, view.some-byod.net),
// first-party or not. Used where a token could be minted for an origin (/account/origin-ticket):
// no `<label>.*` origin may ever be vouched for, whatever zone it is in. A malformed host is
// treated as a sandbox host here (refuse), the opposite default of hostRole, because this check
// guards a grant, not a restriction.
export function isSandboxLabelHost(host, label = DEFAULT_SANDBOX_LABEL) {
	const h = parseHost(host);
	if (!h) return true;
	return h.name.split(".")[0] === normLabel(label);
}
